<!-- Page Header -->
<div class="mb-4">
    <h1 class="h2 mb-1 fw-bold">{{ getGreeting() }}, {{ getUserFirstName() }}</h1>
    <p class="text-muted mb-0">Here's what's happening with your drone operations today.</p>
  </div>

  <!-- Unified Onboarding Card -->
  <div class="mb-4">
    <app-unified-onboarding></app-unified-onboarding>
  </div>

  <!-- Stats Cards Row -->
  <div class="row g-3 mb-4">
    <!-- Drones (combined Total / Active) -->
    <div class="col-12 col-sm-6 col-lg-3">
      <div class="card stat-card h-100">
        <div class="card-body p-3 d-flex flex-column">
          <div class="d-flex align-items-center gap-2 mb-2">
            <i class="icon-drone text-primary fs-5"></i>
            <span class="text-muted small fw-medium">Drones</span>
          </div>
          <div class="mt-auto">
            <ng-container *ngIf="isLoadingStats() && isInitializing(); else dronesValue">
              <div class="skeleton-loader stat-skeleton"></div>
            </ng-container>
            <ng-template #dronesValue>
              <h3 class="stat-value fw-bold mb-0">{{ stats().activeDrones }} <span class="fw-normal text-muted stat-unit">/ {{ stats().totalDrones }}</span></h3>
              <p class="text-muted mb-0 small">Active / Total</p>
            </ng-template>
          </div>
        </div>
      </div>
    </div>

    <!-- Flight Hours + Acres Sprayed (combo) -->
    <div class="col-12 col-sm-6 col-lg-3">
      <div class="card stat-card h-100">
        <div class="card-body p-3 d-flex flex-column">
          <div class="d-flex align-items-center gap-2 mb-2">
            <i class="bi bi-clock text-info fs-5"></i>
            <span class="text-muted small fw-medium">Flight Hours &amp; Acres</span>
          </div>
          <div class="mt-auto">
            <ng-container *ngIf="isLoadingStats() && isInitializing(); else flightAcresValue">
              <div class="skeleton-loader stat-skeleton"></div>
            </ng-container>
            <ng-template #flightAcresValue>
              <h3 class="stat-value fw-bold mb-0">{{ stats().flightHours | number:'1.1-1' }} <span class="fw-normal text-muted stat-unit">hrs</span></h3>
              <p class="mb-0 small">
                <span class="fw-bold">{{ stats().totalAcresSprayed | number:'1.0-0' }}</span>
                <span class="text-muted"> acres sprayed</span>
              </p>
            </ng-template>
          </div>
        </div>
      </div>
    </div>

    <!-- Upcoming Flights -->
    <div class="col-12 col-sm-6 col-lg-3">
      <div class="card stat-card h-100">
        <div class="card-body p-3 d-flex flex-column">
          <div class="d-flex align-items-center gap-2 mb-2">
            <i class="bi bi-calendar-event text-warning fs-5"></i>
            <span class="text-muted small fw-medium">Upcoming Flights</span>
          </div>
          <div class="mt-auto">
            <ng-container *ngIf="isLoadingStats() && isInitializing(); else upcomingFlightsValue">
              <div class="skeleton-loader stat-skeleton"></div>
            </ng-container>
            <ng-template #upcomingFlightsValue>
              <h3 class="stat-value fw-bold mb-0">{{ stats().upcomingFlights }}</h3>
              <p class="text-muted mb-0 small">Scheduled</p>
            </ng-template>
          </div>
        </div>
      </div>
    </div>

    <!-- Acre Advantage Points Snapshot -->
    <div class="col-12 col-sm-6 col-lg-3">
      <div class="card stat-card stat-card-clickable h-100" role="button" routerLink="/acres-advantage">
        <div class="card-body p-3 d-flex flex-column">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <div class="d-flex align-items-center gap-2">
              <i class="bi bi-lightbulb-fill fs-5 text-primary"></i>
              <span class="text-muted small fw-medium">Acre Advantage</span>
            </div>
            <i class="bi bi-arrow-right text-muted small"></i>
          </div>
          <div class="mt-auto">
            <ng-container *ngIf="isLoadingAcresAdvantage() && isInitializing(); else acresAdvantageValue">
              <div class="skeleton-loader stat-skeleton"></div>
            </ng-container>
            <ng-template #acresAdvantageValue>
              <ng-container *ngIf="acresAdvantageData() as aaData; else noAcresData">
                <h3 class="stat-value fw-bold mb-0 text-primary">{{ formatNumber(aaData.totalPointsEarned) }} <span class="fw-normal text-muted stat-unit">pts</span></h3>
                <p class="mb-0 small">
                  <span class="fw-bold text-points-missed">{{ formatNumber(aaData.totalPointsMissed) }}</span>
                  <span class="text-muted"> pts missed</span>
                </p>
              </ng-container>
              <ng-template #noAcresData>
                <h3 class="stat-value fw-bold mb-0 text-muted">--</h3>
                <p class="text-muted mb-0 small">No data yet</p>
              </ng-template>
            </ng-template>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Recent Activity Section -->
  <div class="row mb-4">
    <div class="col-12 col-lg-8">
      <!-- h-100 makes the card fill its column, which Bootstrap stretches to
           match the Quick Actions column beside it — so the empty state is at
           least as tall as Quick Actions. It still grows past that as
           activities are added, without stretching Quick Actions (that card
           is content-height). -->
      <div class="card h-100">
        <div class="card-header p-3">
          <h5 class="mb-0">Recent Activity</h5>
        </div>
        <div class="card-body p-3 d-flex flex-column">
          <!-- Loading skeleton for activities -->
          <div *ngIf="isLoadingActivities() && isInitializing()" class="activity-list">
            <div *ngFor="let i of [1, 2, 3]" class="activity-item">
              <div class="activity-icon skeleton-loader skeleton-icon"></div>
              <div class="activity-content">
                <div class="skeleton-loader skeleton-text mb-2"></div>
                <div class="skeleton-loader skeleton-text-sm"></div>
              </div>
            </div>
          </div>

          <!-- Activity list -->
          <div *ngIf="!isLoadingActivities() || !isInitializing()" class="activity-list flex-grow-1 d-flex flex-column">
            <div
              *ngFor="let activity of activities()"
              class="activity-item"
              [class.activity-item-enter]="isNewActivity(activity.id)"
              [class.activity-item-clickable]="hasResourceLink(activity)"
              (click)="hasResourceLink(activity) && navigateToResource(activity)"
              [attr.role]="hasResourceLink(activity) ? 'button' : null"
              [attr.tabindex]="hasResourceLink(activity) ? 0 : null"
            >
              <div class="activity-icon">
                <i [class]="getActivityIconClass(activity)"></i>
              </div>
              <div class="activity-content">
                <p class="activity-message mb-0 fw-medium">{{ activity.message }}</p>
                <small class="text-muted">{{ getRelativeTime(activity.createdAt) }}</small>
              </div>
              <div *ngIf="hasResourceLink(activity)" class="activity-link-indicator">
                <i class="bi bi-chevron-right text-muted"></i>
              </div>
            </div>

            <div *ngIf="activities().length === 0" class="text-muted text-center flex-grow-1 d-flex flex-column align-items-center justify-content-center py-4">
              <i class="bi bi-activity fs-1 d-block mb-2 opacity-50"></i>
              No recent activity
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 col-lg-4 mt-3 mt-lg-0">
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0">Quick Actions</h5>
        </div>
        <div class="card-body p-3">
          <div class="d-grid gap-2">
            <button class="btn btn-primary" (click)="openQuickScheduleModal()">
              <i class="bi bi-plus-circle me-2"></i>
              Schedule Flight
            </button>
            <button class="btn btn-outline-primary" routerLink="/drones">
              <i class="icon-drone me-2"></i>
              View All Drones
            </button>
            <button class="btn btn-outline-primary" routerLink="/spray-records">
              <i class="bi bi-journal-text me-2"></i>
              Flight Logs
            </button>
            <button class="btn btn-outline-primary" routerLink="/customers">
              <i class="bi bi-people me-2"></i>
              Manage Customers
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Feature Sections: Logbook, Store, Maintenance -->
  <div class="row g-3">
    <!-- Logbook Section -->
    <div class="col-12 col-lg-4">
      <div class="card feature-card h-100">
        <div class="card-body p-4">
          <div class="d-flex align-items-center gap-3 mb-3">
            <div class="feature-icon logbook-icon">
              <i class="bi bi-journal-text"></i>
            </div>
            <div>
              <h5 class="mb-0 fw-bold">CropFlight</h5>
              <p class="text-muted mb-0 small">All of your logs in one place</p>
            </div>
          </div>
          <div class="d-flex flex-column">
            <a routerLink="/logbook" class="feature-link d-flex justify-content-between align-items-center">
              <span>Overview</span>
              <i class="bi bi-arrow-right"></i>
            </a>
            <hr class="feature-divider">
            <a routerLink="/logbook/spray-records" class="feature-link d-flex justify-content-between align-items-center">
              <span>Spray Records</span>
              <i class="bi bi-arrow-right"></i>
            </a>
            <hr class="feature-divider">
            <a routerLink="/certifications" class="feature-link d-flex justify-content-between align-items-center">
              <span>Certifications</span>
              <i class="bi bi-arrow-right"></i>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- CropSwipe App Section — temporarily replaces the Store card to
         promote the newly released mobile apps. The download badges were
         pulled out of the sidebar and live here now. Restore the Store card
         (see git history) when the storefront launches. -->
    <div class="col-12 col-lg-4">
      <div class="card feature-card h-100">
        <div class="card-body p-4">
          <div class="d-flex align-items-center gap-3 mb-3">
            <div class="feature-icon mobile-icon">
              <i class="bi bi-phone"></i>
            </div>
            <div>
              <h5 class="mb-0 fw-bold">CropSwipe App</h5>
              <p class="text-muted mb-0 small">Now available for iOS &amp; Android</p>
            </div>
          </div>
          <!-- `noreferrer` alongside `noopener` so the outbound click doesn't
               leak the in-app URL to the store host via Referer. -->
          <div class="d-flex flex-column">
            <a [href]="appStoreUrls.apple" target="_blank" rel="noopener noreferrer" class="feature-link d-flex justify-content-between align-items-center">
              <span class="d-flex align-items-center gap-2">
                <i class="bi bi-apple"></i>
                Download on the App Store
              </span>
              <i class="bi bi-arrow-right"></i>
            </a>
            <hr class="feature-divider">
            <a [href]="appStoreUrls.google" target="_blank" rel="noopener noreferrer" class="feature-link d-flex justify-content-between align-items-center">
              <span class="d-flex align-items-center gap-2">
                <i class="bi bi-google-play"></i>
                Get it on Google Play
              </span>
              <i class="bi bi-arrow-right"></i>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Maintenance Section -->
    <div class="col-12 col-lg-4">
      <div class="card feature-card h-100">
        <div class="card-body p-4">
          <div class="d-flex align-items-center gap-3 mb-3">
            <div class="feature-icon maintenance-icon">
              <i class="bi bi-tools"></i>
            </div>
            <div>
              <h5 class="mb-0 fw-bold">Maintenance</h5>
              <p class="text-muted mb-0 small">Getting you back in flight</p>
            </div>
          </div>
          <div class="d-flex flex-column">
            <a routerLink="/maintenance" class="feature-link d-flex justify-content-between align-items-center">
              <span>Fix in the Field Videos</span>
              <i class="bi bi-arrow-right"></i>
            </a>
            <hr class="feature-divider">
            <a routerLink="/maintenance/service-appointments" class="feature-link d-flex justify-content-between align-items-center">
              <span>Set an Appt</span>
              <i class="bi bi-arrow-right"></i>
            </a>
            <hr class="feature-divider">
            <a routerLink="/maintenance" class="feature-link d-flex justify-content-between align-items-center">
              <span>Area Training Schedule</span>
              <i class="bi bi-arrow-right"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>

<!-- Quick Schedule Modal -->
<app-quick-schedule-modal
  #quickScheduleModal
  (scheduled)="onFlightScheduled($event)"
></app-quick-schedule-modal>
